<template>
  <div class="flex-1 overflow-y-auto p-8 space-y-6">

    <!-- ================= TOP PLATFORM METRICS ================= -->

    <div class="grid grid-cols-1 md:grid-cols-4 gap-6">

      <div class="admin-card">
        <span>Total Businesses</span>
        <strong>{{ stats.total_business }}</strong>
        <small class="positive">Platform Growth</small>
      </div>

      <div class="admin-card">
        <span>Active Subscriptions</span>
        <strong>{{ stats.active_plans }}</strong>
        <small class="positive">Revenue Drivers</small>
      </div>

      <div class="admin-card">
        <span>Total Users</span>
        <strong>{{ stats.total_users }}</strong>
        <small class="positive">User Base</small>
      </div>

      <div class="admin-card">
        <span>Total Reviews</span>
        <strong>{{ stats.total_reviews }}</strong>
        <small class="warning">Engagement Signal</small>
      </div>

    </div>

    <!-- ================= REVENUE + GROWTH ================= -->

    <div class="">

      <!-- Revenue Trend -->
      <div class="admin-panel">

        <div class="panel-header">
          <div>
            <h3>Revenue Growth</h3>
            <p>MRR Performance</p>
          </div>

          <button class="ghost-btn small">Export</button>
        </div>

        <div class="chart-wrapper">
          <div id="revenueChart"></div>
        </div>

      </div>

    

    </div>

    <!-- ================= USAGE + RISKS ================= -->

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

      <!-- Platform Usage -->
      <div class="admin-panel">

        <h3>Platform Usage</h3>

        <div class="chart-wrapper">
          <div id="usageChart"></div>
        </div>

      </div>

      <!-- Review Volume -->
      <div class="admin-panel">

        <h3>Review Volume</h3>

        <div class="chart-wrapper">
          <div id="reviewVolumeChart"></div>
        </div>

      </div>

      <!-- Risk Signals -->
      <div class="admin-panel">

        <h3>Risk Signals</h3>

        <div class="risk-list">

          <div>
            <span>Negative Reviews</span>
            <strong class="danger">{{ risks.negative_reviews }}</strong>
          </div>

          <div>
            <span>Positive Reviews</span>
            <strong class="warning">{{ risks.positive_reviews }}</strong>
          </div>

          <div>
            <span>Deleted QRs</span>
            <strong>{{ risks.deleted_qrs }}</strong>
          </div>

        </div>

      </div>

    </div>

  </div>
</template>
<script>
import ApexCharts from "apexcharts";

import {
  getAdminDashboardStats,
  getRevenueTrend,
  getUsageBreakdown,
  getReviewVolume,
  getRiskSignals
} from "../apis/dashapis1";

export default {

  data() {
    return {

      stats: {
        total_users: 0,
        total_business: 0,
        total_reviews: 0,
        active_plans: 0
      },

      risks: {},

      revenueChart: null,
      usageChart: null,
      reviewChart: null
    };
  },

  async mounted() {

    await this.loadDashboard();
  },

  methods: {

    async loadDashboard() {

      await Promise.all([
        this.loadStats(),
        this.loadRevenueTrend(),
        this.loadUsage(),
        this.loadReviewVolume(),
        this.loadRisks()
      ]);
    },

    /* ================= STATS ================= */

    async loadStats() {

      const res = await getAdminDashboardStats();

      if (res.status) {
        this.stats = res.data;
      }
    },

    /* ================= REVENUE ================= */

    async loadRevenueTrend() {

      const res = await getRevenueTrend();

      if (!res.status) return;

      const categories = res.data.map(r => {
        const d = new Date(r.dt);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });
      const seriesData = res.data.map(r => Number(r.revenue));

      if (this.revenueChart) {
        this.revenueChart.updateOptions({
          series: [{ name: "Revenue", data: seriesData }],
          xaxis: { categories }
        });
        return;
      }

      this.revenueChart = new ApexCharts(
  document.querySelector("#revenueChart"),
  {
    chart: {
      type: "line",
      height: 260,
      toolbar: { show: false }
    },

    series: [
      { name: "Revenue", data: seriesData }
    ],

    stroke: {
      curve: "smooth",
      width: 3
    },

    markers: {
      size: 0,
      hover: {
        size: 0
      }
    },

    dataLabels: {
      enabled: false
    },

    colors: ["#197fe6"],

    xaxis: {
      categories
    },

    grid: {
      borderColor: "#e2e8f0"
    }
  }
);

this.revenueChart.render();
    },

    /* ================= USAGE ================= */

    async loadUsage() {

      const res = await getUsageBreakdown();

      if (!res.status) return;

      const labels = res.data.map(r => r.plan);
      const series = res.data.map(r => Number(r.total));

      if (this.usageChart) {
        this.usageChart.updateOptions({ labels, series });
        return;
      }

      this.usageChart = new ApexCharts(
        document.querySelector("#usageChart"),
        {
          chart: { type: "donut", height: 260 },
          series,
          labels,
          colors: ["#22c55e", "#38bdf8", "#f59e0b"],
          legend: { position: "bottom" }
        }
      );

      this.usageChart.render();
    },

    /* ================= REVIEW VOLUME ================= */

    async loadReviewVolume() {

      const res = await getReviewVolume();

      if (!res.status) return;

      const categories = res.data.map(r => {
        const d = new Date(r.dt);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });
      const seriesData = res.data.map(r => Number(r.reviews));

      if (this.reviewChart) {
        this.reviewChart.updateOptions({
          series: [{ name: "Reviews", data: seriesData }],
          xaxis: { categories }
        });
        return;
      }

      this.reviewChart = new ApexCharts(
        document.querySelector("#reviewVolumeChart"),
        {
          chart: { type: "bar", height: 260, toolbar: { show: false }},
          series: [{ name: "Reviews", data: seriesData }],
          xaxis: { categories },
          colors: ["#197fe6"]
        }
      );

      this.reviewChart.render();
    },

    /* ================= RISKS ================= */

    async loadRisks() {

      const res = await getRiskSignals();

      if (res.status) {
        this.risks = res.data;
      }
    }
  }
};
</script>
<style scoped>

.admin-card {
  background: white;
  padding: 18px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.admin-card span {
  font-size: 12px;
  color: #64748b;
}

.admin-card strong {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
}

.admin-panel {
  background: white;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.panel-header h3 {
  font-weight: 700;
}

.panel-header p {
  font-size: 12px;
  color: #94a3b8;
}

.health-list,
.risk-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;
}

.health-list div,
.risk-list div {
  display: flex;
  justify-content: space-between;
}

.positive { color: #16a34a; }
.warning { color: #f59e0b; }
.danger { color: #ef4444; }

.chart-wrapper {
  margin-top: 10px;
}

/* MOBILE */

@media(max-width:768px) {
  .admin-card strong { font-size: 20px; }
}
/* ================= CHART CONTAINERS ================= */

.chart-wrapper {
  position: relative;
  width: 100%;
  height: 260px;
  overflow: hidden;              /* ✅ prevents bleed */
}

/* Apex canvas sizing fix */

.chart-wrapper .apexcharts-canvas {
  max-width: 100% !important;
}

/* SVG containment */

.chart-wrapper svg {
  width: 100% !important;
}

/* Prevent weird flex stretching */

.admin-panel,
.admin-card {
  min-width: 0;                  /* ✅ VERY important inside grid/flex */
}

/* Fix grid overflow edge cases */

.grid {
  width: 100%;
  margin: 0;
}

/* ================= SAFETY FOR SMALL SCREENS ================= */

@media (max-width: 1024px) {

  .chart-wrapper {
    height: 220px;               /* Slightly smaller */
  }

}

/* ================= SAFETY FOR MOBILE ================= */

@media (max-width: 640px) {

  .chart-wrapper {
    height: 200px;
  }

}

</style>
